import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../context/ConfirmContext';

import { BACKEND_URL } from '../config';

const getLogoUrl = (company, cacheBuster) => {
  if (!company?.logo_url) return null;
  const base = `${BACKEND_URL}${company.logo_url}`;
  return cacheBuster ? `${base}?t=${cacheBuster}` : base;
};


const Companies = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    unique_number: '',
    tvsh_number: '',
    status: 'ACTIVE'
  });

  // Long press (3 sec) on logo to upload
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const fileInputRef = useRef(null);
  const modalLogoInputRef = useRef(null);
  const createLogoInputRef = useRef(null);
  const [createLogoFile, setCreateLogoFile] = useState(null);
  const [logoPreviewKey, setLogoPreviewKey] = useState(0); // pas ngarkimit për të rifreskuar parapamjen
  const longPressTimerRef = useRef(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  // ESC = back: close modal first, else go to /me
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showPhotoModal) setShowPhotoModal(false);
      else if (showModal) setShowModal(false);
      else navigate('/me');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, showPhotoModal, navigate]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Long press 3 seconds on logo to open upload
  const LONG_PRESS_MS = 3000;
  const handleLogoPointerDown = (companyId) => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPhotoTarget({ type: 'company', id: companyId });
      setShowPhotoModal(true);
    }, LONG_PRESS_MS);
  };
  const handleLogoPointerUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const handleLogoPointerLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoTarget) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.png')) {
      toast.error(t('pngOnly'));
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/upload/photo/${photoTarget.type}/${photoTarget.id}`,
        formDataUpload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchCompanies();
      setShowPhotoModal(false);
      toast.success(t('logoUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleModalLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingCompany?.id) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.png')) {
      toast.error(t('pngOnly'));
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await axios.post(
        `${BACKEND_URL}/api/upload/photo/company/${editingCompany.id}`,
        formDataUpload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newLogoUrl = res.data?.logo_url;
      if (newLogoUrl) {
        setEditingCompany((prev) => (prev ? { ...prev, logo_url: newLogoUrl } : null));
        setLogoPreviewKey(Date.now());
      }
      const response = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setCompanies(list);
      toast.success(t('logoUploaded'));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail ?? err.response?.data?.message ?? err.message;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
      let fullMsg = `Gabim gjatë ruajtjes së logos: ${status ? `[${status}] ` : ''}${msg}`;
      if (status === 405) {
        fullMsg += '\n\nPër ngarkim përdorni backend-in lokal (REACT_APP_BACKEND_URL=http://127.0.0.1:8003) dhe rifresko faqen.';
      }
      toast.error(fullMsg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    setCreateLogoFile(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      unique_number: '',
      tvsh_number: '',
      status: 'ACTIVE'
    });
    setShowModal(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setLogoPreviewKey(0);
    setFormData({
      name: company.name,
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      unique_number: company.unique_number || '',
      tvsh_number: company.tvsh_number || '',
      status: company.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    const payload = {
      name: formData.name,
      address: formData.address || null,
      phone: formData.phone || null,
      email: formData.email || null,
      website: formData.website || null,
      unique_number: formData.unique_number || null,
      tvsh_number: formData.tvsh_number || null,
      status: formData.status
    };
    try {
      if (editingCompany) {
        await axios.put(`${BACKEND_URL}/api/companies/${editingCompany.id}`, payload, { headers });
        setShowModal(false);
        fetchCompanies();
        return;
      }
      // Krijo kompani të re
      const createRes = await axios.post(`${BACKEND_URL}/api/companies`, payload, { headers });
      const newCompany = createRes.data;
      const newId = newCompany?.id;
      if (newId && createLogoFile) {
        const name = (createLogoFile.name || '').toLowerCase();
        if (name.endsWith('.png')) {
          try {
            const fd = new FormData();
            fd.append('file', createLogoFile);
            await axios.post(`${BACKEND_URL}/api/upload/photo/company/${newId}`, fd, { headers });
          } catch (photoErr) {
            const status = photoErr.response?.status;
            const detail = photoErr.response?.data?.detail ?? photoErr.message;
            toast.error(`Kompania u ruajt, por logos i dështoi ngarkimi: ${status ? `[${status}] ` : ''}${detail}`);
          }
        }
      }
      setCreateLogoFile(null);
      setShowModal(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('companySaveError'));
    }
  };

  const handleDelete = async (companyId) => {
    const ok = await confirm({ title: t('confirmDeleteTitle'), message: t('confirmDeleteCompany'), confirmLabel: t('delete'), cancelLabel: t('cancel') });
    if (!ok) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}`);
      fetchCompanies();
    } catch (error) {
      const isNetwork = !error.response || (error.message && String(error.message).toLowerCase().includes('network'));
      const msg = isNetwork ? 'Lidhja me serverin dështoi. Kontrolloni që backend-i të jetë i ndezur.' : (error.response?.data?.detail || 'Gabim gjatë fshirjes së kompanisë.');
      toast.error(msg);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{t('companiesTitle')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('companiesSubtitle')}</p>
            </div>
            <Link to="/me" className="md:hidden inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm md:text-base shrink-0">
              <span>{t('back')}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
          </div>
          <div className="w-full md:w-auto md:flex md:justify-end">
            <button
              onClick={handleCreate}
              className="w-full md:w-auto px-4 py-2.5 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm md:text-base"
            >
              {t('addCompany')}
            </button>
          </div>
        </div>

        {/* Stats - si te Klientët */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('totalCompanies')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{companies.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Aktive</p>
            <p className="text-2xl font-bold text-green-600">{companies.filter((c) => c.status === 'ACTIVE').length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 col-span-2 md:col-span-1">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Joaktive</p>
            <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">{companies.filter((c) => c.status !== 'ACTIVE').length}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-sm md:text-base text-gray-500 dark:text-gray-400">Duke ngarkuar...</div>
        ) : companies.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center shadow-sm border border-gray-200 dark:border-gray-700">
            <span className="text-5xl opacity-50 block mb-3">🏢</span>
            <p className="text-gray-500 dark:text-gray-400">Nuk ka kompani të regjistruara</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Shtoni kompaninë e parë për të filluar</p>
          </div>
        ) : (
          <>
            {/* Tabelë për desktop (md+) */}
            <div className="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                  <thead className="text-xs uppercase bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                    <tr>
                      <th scope="col" className="px-4 py-3 rounded-tl-lg">Logo</th>
                      <th scope="col" className="px-4 py-3">Emri i kompanisë</th>
                      <th scope="col" className="px-4 py-3">Adresa</th>
                      <th scope="col" className="px-4 py-3">Telefoni</th>
                      <th scope="col" className="px-4 py-3">Email</th>
                      <th scope="col" className="px-4 py-3">Nr. Unik</th>
                      <th scope="col" className="px-4 py-3">Status</th>
                      <th scope="col" className="px-4 py-3 rounded-tr-lg text-right">Veprime</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {companies.map((company) => {
                      const logoUrl = getLogoUrl(company);
                      return (
                        <tr key={company.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                          <td className="px-4 py-3">
                            <div
                              role="button"
                              tabIndex={0}
                              onPointerDown={() => handleLogoPointerDown(company.id)}
                              onPointerUp={handleLogoPointerUp}
                              onPointerLeave={handleLogoPointerLeave}
                              onContextMenu={(e) => e.preventDefault()}
                              className="w-12 h-12 flex items-center justify-center cursor-pointer hover:opacity-80 transition flex-shrink-0 rounded-lg overflow-hidden bg-transparent dark:bg-gray-700"
                              title="Mbaj shtypur 3 sekonda për të ngarkuar logo (PNG)"
                            >
                              {logoUrl ? (
                                <img src={logoUrl} alt={company.name} className="w-full h-full object-contain" />
                              ) : (
                                <span className="text-2xl opacity-50">🏢</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">{company.name}</td>
                          <td className="px-4 py-3 max-w-[180px] truncate" title={company.address || ''}>{company.address || '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{company.phone || '—'}</td>
                          <td className="px-4 py-3 max-w-[180px] truncate" title={company.email || ''}>{company.email || '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{company.unique_number || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${company.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                              {company.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button onClick={() => handleEdit(company)} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium">
                                Ndrysho
                              </button>
                              <button onClick={() => handleDelete(company.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium">
                                Fshi
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Kartela për mobile – e njëjta dizajn si më parë, e përshtatshme për ekran të vogël */}
            <div className="md:hidden grid grid-cols-1 gap-3">
              {companies.map((company) => {
                const logoUrl = getLogoUrl(company);
                return (
                  <div key={company.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex justify-between items-start gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{company.name}</h3>
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full mt-2 ${company.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                          {company.status}
                        </span>
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        onPointerDown={() => handleLogoPointerDown(company.id)}
                        onPointerUp={handleLogoPointerUp}
                        onPointerLeave={handleLogoPointerLeave}
                        onContextMenu={(e) => e.preventDefault()}
                        className="w-16 h-16 flex items-center justify-center cursor-pointer hover:opacity-80 transition flex-shrink-0 rounded-lg overflow-hidden bg-transparent dark:bg-gray-700"
                        title="Mbaj shtypur 3 sekonda për të ngarkuar logo (PNG)"
                      >
                        {logoUrl ? (
                          <img src={logoUrl} alt={company.name} className="max-w-full max-h-full object-contain" />
                        ) : (
                          <span className="text-3xl opacity-50">🏢</span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {company.address && (
                        <p className="flex items-center gap-2 truncate">
                          <span>📍</span>
                          <span className="truncate">{company.address}</span>
                        </p>
                      )}
                      {company.phone && (
                        <p className="flex items-center gap-2">
                          <span>📞</span>
                          <span>{company.phone}</span>
                        </p>
                      )}
                      {company.email && (
                        <p className="flex items-center gap-2 truncate">
                          <span>📧</span>
                          <span className="truncate">{company.email}</span>
                        </p>
                      )}
                      {company.unique_number && (
                        <p className="flex items-center gap-2">
                          <span>🆔</span>
                          <span>{company.unique_number}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <button onClick={() => handleEdit(company)} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                        Ndrysho
                      </button>
                      <button onClick={() => handleDelete(company.id)} className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
                        Fshi
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Modal - mobile: scroll, pjesë e bardhë pak më e vogël; desktop: si më parë */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-start md:items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-sm md:max-w-2xl shadow-xl min-h-[62vh] max-h-[78vh] md:max-h-[82vh] md:min-h-0 overflow-y-auto flex flex-col" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 p-2 md:p-4 border-b border-gray-200 dark:border-gray-700 z-10 shrink-0">
                <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
                  {editingCompany ? t('editCompany') : t('addNewCompany')}
                </h3>
                <button type="button" onClick={() => { setCreateLogoFile(null); setShowModal(false); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600" aria-label={t('close')}>✕</button>
              </div>
              <form onSubmit={handleSubmit} className="p-2 md:p-4 pb-24 md:pb-4">
                {/* Logo e kompanisë (PNG) – zonë e vogël */}
                {!editingCompany && (
                  <div className="mb-2 md:mb-3 flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 shrink-0">Logo (PNG, opsionale)</label>
                    <input ref={createLogoInputRef} type="file" accept=".png,image/png" onChange={(e) => setCreateLogoFile(e.target.files?.[0] || null)} className="hidden" />
                    <button type="button" onClick={() => createLogoInputRef.current?.click()}
                      className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition">
                      {createLogoFile ? createLogoFile.name.slice(0, 18) + (createLogoFile.name.length > 18 ? '…' : '') : t('selectLogo')}
                    </button>
                  </div>
                )}
                {editingCompany && (
                  <div className="mb-2 md:mb-3 flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 shrink-0">Logo kompanie</label>
                    <div className="w-10 h-10 flex items-center justify-center rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shrink-0 overflow-hidden">
                      {getLogoUrl(editingCompany, logoPreviewKey) ? (
                        <img src={getLogoUrl(editingCompany, logoPreviewKey)} alt="" className="w-full h-full object-contain" key={logoPreviewKey} />
                      ) : (
                        <span className="text-lg opacity-50">🏢</span>
                      )}
                    </div>
                    <input ref={modalLogoInputRef} type="file" accept=".png,image/png" onChange={handleModalLogoUpload} className="hidden" />
                    <button type="button" onClick={() => modalLogoInputRef.current?.click()} disabled={uploading}
                      className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition">
                      {uploading ? '...' : 'Ngarko / Ndrysho'}
                    </button>
                  </div>
                )}
                {/* Web: gjysa majtas, gjysa djathtas; të gjitha fushat (tabelat) të njëjtin dimension; mobile: një kolonë */}
                <div className="space-y-1 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-3">
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Emri i kompanisë *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('companyNamePlaceholder')}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" required />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0">
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Adresa</label>
                    <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Telefoni</label>
                    <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Email</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Website</label>
                    <input type="text" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Nr. Unik</label>
                    <input type="text" value={formData.unique_number} onChange={(e) => setFormData({ ...formData, unique_number: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-0.5">Nr. TVSH</label>
                    <input type="text" value={formData.tvsh_number} onChange={(e) => setFormData({ ...formData, tvsh_number: e.target.value })}
                      className="w-full min-w-0 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2rem] h-8 max-h-8 md:h-10 md:min-h-10 md:max-h-10 md:py-0" />
                  </div>
                </div>
                <div className="flex gap-2 mt-2 md:mt-4 md:pt-2">
                  <button type="button" onClick={() => { setCreateLogoFile(null); setShowModal(false); }}
                    className="flex-1 h-8 md:py-2.5 px-3 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg text-sm font-medium">{t('cancel')}</button>
                  <button type="submit" className="flex-1 h-8 md:py-2.5 px-3 bg-blue-600 text-white rounded-lg text-sm font-medium">{t('save')}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Photo Upload Modal - mobile: në mes, X */}
        {showPhotoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">Ngarko Logo</h3>
                <button type="button" onClick={() => setShowPhotoModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label={t('close')}>✕</button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Zgjidh një foto PNG (pa background) për logon e kompanisë. Logo shfaqet vetëm këtu dhe në mobile.</p>
              <input ref={fileInputRef} type="file" accept=".png,image/png" onChange={handlePhotoUpload} className="hidden" />
              <div className="flex gap-3">
                <button onClick={() => setShowPhotoModal(false)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                  Kthehu
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                  {uploading ? t('loading') : t('selectImage')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Companies;
